import { useState, useRef, useCallback, useEffect } from 'react'
import { SectionId } from './constants'

const SCROLL_ANIMATION_DURATION = 500
const SCROLL_DEBOUNCE_DELAY = 100
const SECTION_ORDER: SectionId[] = ['eligibility', 'howToClaim', 'notIncluded', 'coverage', 'faqs']

type SectionRefs = { [key in SectionId]: HTMLDivElement | null }

export const useSectionObserver = () => {
  const [activeSection, setActiveSection] = useState<SectionId>('eligibility')
  const [isScrolling, setIsScrolling] = useState(false)

  const sectionRefs = useRef<SectionRefs>({
    eligibility: null,
    howToClaim: null,
    notIncluded: null,
    coverage: null,
    faqs: null
  })
  const contentScrollRef = useRef<HTMLDivElement | null>(null)
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const rafRef = useRef<number | null>(null)

  const scrollToSection = useCallback((sectionId: SectionId) => {
    setIsScrolling(true)
    setActiveSection(sectionId)
    
    const sectionElement = sectionRefs.current[sectionId]
    const scrollContainer = contentScrollRef.current
    
    if (sectionElement && scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect()
      const sectionRect = sectionElement.getBoundingClientRect()
      const scrollTop = scrollContainer.scrollTop + sectionRect.top - containerRect.top - 20
      
      scrollContainer.scrollTo({ top: scrollTop, behavior: 'smooth' })
      setTimeout(() => setIsScrolling(false), SCROLL_ANIMATION_DURATION)
    }
  }, [])

  // Determine which section is currently most visible based on scroll position
  const updateActiveSection = useCallback(() => {
    if (isScrolling) return
    
    const scrollContainer = contentScrollRef.current
    if (!scrollContainer) return

    const containerTop = scrollContainer.getBoundingClientRect().top
    const scrollOffset = 100 // Offset to determine when a section becomes "active"
    
    let currentSection: SectionId = 'eligibility'
    let minDistance = Infinity

    // Find the section whose top is closest to the container top (within viewport)
    SECTION_ORDER.forEach((sectionId) => {
      const sectionElement = sectionRefs.current[sectionId]
      if (!sectionElement) return

      const sectionRect = sectionElement.getBoundingClientRect()
      const sectionTop = sectionRect.top - containerTop
      
      // Calculate distance from the scroll offset point
      const distance = Math.abs(sectionTop - scrollOffset)
      
      // If this section is within viewport and closer than previous ones
      if (sectionTop <= scrollOffset + sectionRect.height && distance < minDistance) {
        minDistance = distance
        currentSection = sectionId
      }
    })

    setActiveSection(currentSection)
  }, [isScrolling])

  // Handle scroll events with debouncing
  useEffect(() => {
    const scrollContainer = contentScrollRef.current
    if (!scrollContainer) return

    const handleScroll = () => {
      // Clear existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }

      // Cancel any pending animation frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      // Use requestAnimationFrame for smooth updates
      rafRef.current = requestAnimationFrame(() => {
        // Debounce the actual state update
        scrollTimeoutRef.current = setTimeout(() => {
          updateActiveSection()
        }, SCROLL_DEBOUNCE_DELAY)
      })
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })
    
    // Initial check
    updateActiveSection()

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current)
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [updateActiveSection])

  const attachSectionRef = useCallback((sectionId: SectionId) => (el: HTMLDivElement | null) => {
    sectionRefs.current[sectionId] = el
  }, [])

  const setScrollContainerRef = useCallback((el: HTMLDivElement | null) => {
    contentScrollRef.current = el
  }, [])

  return {
    activeSection,
    scrollToSection,
    attachSectionRef,
    setScrollContainerRef
  }
}

